"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
  SymbolChart,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { foreignLetters, lettersOnly } from "@/lib/tool-kit/ciphers/alphabet";
import {
  type BaconAlphabet,
  type BaconDecodeResult,
  baconBits,
  baconLetters,
  type CoverRun,
  type CoverStyle,
  coverToHtml,
  coverToText,
  customSymbols,
  hideInCover,
  messageBits,
  runBacon,
  styleCoverRun,
  writeBaconBits,
} from "@/lib/tool-kit/ciphers/bacon";

type SymbolScheme = "ab" | "01" | "custom" | "case";

const SCHEME_SYMBOLS: Record<"ab" | "01", readonly [string, string]> = {
  ab: ["A", "B"],
  "01": ["0", "1"],
};
const MERGED_LABELS: Readonly<Record<string, string>> = { I: "I/J", U: "U/V" };
const LOWER_AB: readonly [string, string] = ["a", "b"];

/** English strings. A translated content file overrides these through `ui`. */
export const BACON_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Message",
  codeText: "Bacon code",
  decodedText: "Decoded message",
  inputPlaceholder: "Type a message, or paste a/b code to decode",
  sample: "Bacon",
  alphabet: "Alphabet",
  alphabet24: "Bacon's 24 letters (I = J, U = V)",
  alphabet26: "26 letters, one code each",
  symbols: "Symbols",
  symbolsAB: "A and B",
  symbols01: "0 and 1",
  symbolsCustom: "Custom symbols",
  symbolsCase: "Letter case (lowercase = a, capital = b)",
  symbolA: "Symbol for a",
  symbolB: "Symbol for b",
  symbolError: "Choose two different single characters as the symbols.",
  group: "Write in groups of five",
  lettersOnly:
    "Only letters are encoded: Bacon's alphabet has no codes for spaces, digits or punctuation.",
  foreign:
    "one: {count} letter outside A–Z was skipped: {letters}|other: {count} letters outside A–Z were skipped: {letters}",
  readRule:
    "Everything except the two symbols is ignored, so spacing and line breaks do not matter. Letters are matched in either case.",
  invalid:
    "one: {count} group stands for no letter in this alphabet and is shown as a question mark.|other: {count} groups stand for no letter in this alphabet and are shown as question marks.",
  leftover:
    "one: {count} symbol was left over after the last full group of five.|other: {count} symbols were left over after the last full group of five.",
  mergedNote:
    "In Bacon's alphabet I and J share a code, as do U and V: read each I as I or J, and each U as U or V.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  working: "The biliteral alphabet",
  chartLabel: "Each letter and its five-symbol code",
  chartHint:
    "Each code is the letter's place in the alphabet counted from zero and written in binary, with a for 0 and b for 1. Letters in your message are highlighted.",
  coverHeading: "Hide it in plain sight",
  coverIntro:
    "Bacon's real idea: write any innocent text in two styles. Each letter of it carries one symbol — ordinary for a, the second style for b. Spaces and punctuation carry nothing.",
  coverLabel: "Cover text",
  coverSample:
    "The roses in the garden have been lovely all week, despite the rain.",
  coverStyle: "Second style",
  bold: "Bold",
  italic: "Italic",
  capitals: "Capitals",
  coverPreview: "Your message hidden in the cover text",
  coverShort:
    "one: The message needs {count} letter of cover text and this one has {carriers}. Add more text to hide all of it.|other: The message needs {count} letters of cover text and this one has {carriers}. Add more text to hide all of it.",
  coverFits:
    "one: The message is carried by the first {count} letter of the cover text; the rest stay plain.|other: The message is carried by the first {count} letters of the cover text; the rest stay plain.",
  coverRead:
    "To read it back, write a for each plain letter and b for each styled one, then decode the groups of five. Capitals survive plain-text copying: paste the result into Decode and choose letter case.",
  coverEmpty: "Type a message above to hide it here.",
  copyStyled: "Copy the styled text",
} as const;

type Strings = typeof BACON_CIPHER_STRINGS;

function RunText({ run, style }: { run: CoverRun; style: CoverStyle }) {
  const text = styleCoverRun(run, style);
  if (!run.marked || style === "capitals") {
    return <span>{text}</span>;
  }
  return style === "bold" ? <strong>{text}</strong> : <em>{text}</em>;
}

function CoverDemo({
  bits,
  strings,
}: {
  bits: readonly number[];
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [cover, setCover] = useState<string>(strings.coverSample);
  const [style, setStyle] = useState<CoverStyle>("bold");
  const hidden = hideInCover(bits, cover);
  const plain = coverToText(hidden.runs, style);
  const counts = { count: fmt(hidden.needed), carriers: fmt(hidden.carriers) };
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.coverHeading}</h2>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {strings.coverIntro}
      </p>
      <TextAreaField
        id="bc-cover"
        label={strings.coverLabel}
        onChange={setCover}
        prose
        rows={3}
        value={cover}
      />
      <PillGroup
        label={strings.coverStyle}
        name="bc-style"
        onChange={setStyle}
        options={[
          { value: "bold", label: strings.bold },
          { value: "italic", label: strings.italic },
          { value: "capitals", label: strings.capitals },
        ]}
        value={style}
      />
      {bits.length === 0 ? (
        <Notice>{strings.coverEmpty}</Notice>
      ) : (
        <div aria-live="polite" className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.coverPreview}
          </p>
          <p className="whitespace-pre-wrap rounded-lg border border-border bg-background p-4 text-base text-foreground leading-relaxed">
            {hidden.runs.map((run, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: runs are positional
              <RunText key={index} run={run} style={style} />
            ))}
          </p>
          <p className={HINT}>
            {tPlural(
              hidden.carriers < hidden.needed
                ? strings.coverShort
                : strings.coverFits,
              locale,
              hidden.needed,
              counts
            )}
          </p>
          <p className={HINT}>{strings.coverRead}</p>
          <CopyButton
            copiedLabel={strings.copied}
            html={
              style === "capitals" ? undefined : coverToHtml(hidden.runs, style)
            }
            label={strings.copyStyled}
            text={plain}
          />
        </div>
      )}
    </section>
  );
}

function schemeSymbols(
  scheme: SymbolScheme,
  custom: readonly [string, string]
): readonly [string, string] | null {
  if (scheme === "ab" || scheme === "01") {
    return SCHEME_SYMBOLS[scheme];
  }
  return scheme === "case" ? LOWER_AB : customSymbols(custom[0], custom[1]);
}

function BaconNotices({
  mode,
  input,
  decoded,
  strings,
}: {
  mode: CipherMode;
  input: string;
  decoded: BaconDecodeResult | null;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (mode === "encode") {
    const foreign = foreignLetters(input);
    return (
      <>
        <p className={HINT}>{strings.lettersOnly}</p>
        {foreign.count > 0 ? (
          <Notice>
            {tPlural(strings.foreign, locale, foreign.count, {
              count: fmt(foreign.count),
              letters: foreign.letters.join(" "),
            })}
          </Notice>
        ) : null}
      </>
    );
  }
  const invalid = decoded?.invalid ?? 0;
  const leftover = decoded?.leftover ?? 0;
  return (
    <>
      <p className={HINT}>{strings.readRule}</p>
      {invalid > 0 ? (
        <Notice>
          {tPlural(strings.invalid, locale, invalid, { count: fmt(invalid) })}
        </Notice>
      ) : null}
      {leftover > 0 ? (
        <Notice>
          {tPlural(strings.leftover, locale, leftover, {
            count: fmt(leftover),
          })}
        </Notice>
      ) : null}
    </>
  );
}

function KeyControls({
  mode,
  alphabet,
  onAlphabet,
  scheme,
  onScheme,
  custom,
  onCustom,
  valid,
  group,
  onGroup,
  strings,
}: {
  mode: CipherMode;
  alphabet: BaconAlphabet;
  onAlphabet: (alphabet: BaconAlphabet) => void;
  scheme: SymbolScheme;
  onScheme: (scheme: SymbolScheme) => void;
  custom: readonly [string, string];
  onCustom: (custom: readonly [string, string]) => void;
  valid: boolean;
  group: boolean;
  onGroup: (group: boolean) => void;
  strings: Strings;
}) {
  const schemeOptions: Option<SymbolScheme>[] = [
    { value: "ab", label: strings.symbolsAB },
    { value: "01", label: strings.symbols01 },
    { value: "custom", label: strings.symbolsCustom },
  ];
  if (mode === "decode") {
    schemeOptions.push({ value: "case", label: strings.symbolsCase });
  }
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.alphabet}
        name="bc-alphabet"
        onChange={onAlphabet}
        options={[
          { value: "24", label: strings.alphabet24 },
          { value: "26", label: strings.alphabet26 },
        ]}
        value={alphabet}
      />
      <SelectField
        id="bc-symbols"
        label={strings.symbols}
        onChange={onScheme}
        options={schemeOptions}
        value={scheme}
      />
      {scheme === "custom" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="bc-symbol-a"
            label={strings.symbolA}
            mono
            onChange={(value) => onCustom([value, custom[1]])}
            value={custom[0]}
          />
          <TextField
            id="bc-symbol-b"
            label={strings.symbolB}
            mono
            onChange={(value) => onCustom([custom[0], value])}
            value={custom[1]}
          />
        </div>
      ) : null}
      {valid ? null : <ErrorNotice>{strings.symbolError}</ErrorNotice>}
      {mode === "encode" ? (
        <Checkbox checked={group} label={strings.group} onChange={onGroup} />
      ) : null}
    </div>
  );
}

export default function BaconCipherWidget() {
  const strings = useToolUi(BACON_CIPHER_STRINGS);
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [alphabet, setAlphabet] = useState<BaconAlphabet>("24");
  const [scheme, setScheme] = useState<SymbolScheme>("ab");
  const [custom, setCustom] = useState<readonly [string, string]>(["x", "y"]);
  const [group, setGroup] = useState(true);

  // Letter case is a way of reading text, not of writing it: encoding falls back to A/B.
  const activeScheme: SymbolScheme =
    mode === "encode" && scheme === "case" ? "ab" : scheme;
  const symbols = schemeSymbols(activeScheme, custom);
  const { output, decoded } = runBacon({
    mode,
    input,
    alphabet,
    symbols,
    readCase: activeScheme === "case",
    group,
  });
  const message = mode === "encode" ? input : output;
  const used = new Set(lettersOnly(message));
  const chart = [...baconLetters(alphabet)].map((letter, value) => ({
    key: alphabet === "24" ? (MERGED_LABELS[letter] ?? letter) : letter,
    value: writeBaconBits(baconBits(value), symbols ?? LOWER_AB, false),
    active: used.has(letter),
  }));

  return (
    <CipherWorkbench
      extras={
        <CoverDemo bits={messageBits(message, alphabet)} strings={strings} />
      }
      idPrefix="bc"
      input={input}
      keyControls={
        <KeyControls
          alphabet={alphabet}
          custom={custom}
          group={group}
          mode={mode}
          onAlphabet={setAlphabet}
          onCustom={setCustom}
          onGroup={setGroup}
          onScheme={setScheme}
          scheme={activeScheme}
          strings={strings}
          valid={symbols !== null}
        />
      }
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.codeText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.codeText : strings.decodedText,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        <BaconNotices
          decoded={decoded}
          input={input}
          mode={mode}
          strings={strings}
        />
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      outputNotes={
        mode === "decode" && alphabet === "24" && output !== "" ? (
          <p className={HINT}>{strings.mergedNote}</p>
        ) : null
      }
      working={
        <div className="space-y-2">
          <SymbolChart items={chart} label={strings.chartLabel} wide />
          <p className={HINT}>{strings.chartHint}</p>
        </div>
      }
    />
  );
}
